import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, getAuthHeader } from '../App';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { DollarSign, Clock, AlertTriangle, CheckCircle, Eye, X, Download, Upload } from 'lucide-react';
import { downloadFile } from '../utils/fileUtils';

const PaymentQueue = () => {
  const navigate = useNavigate();
  const [overdue, setOverdue] = useState([]);
  const [due, setDue] = useState([]);
  const [outstanding, setOutstanding] = useState([]);
  const [onHold, setOnHold] = useState([]);
  const [partlyPaid, setPartlyPaid] = useState([]); // New state for partly paid requests
  const [paid, setPaid] = useState([]);
  const [paidPayments, setPaidPayments] = useState([]); // New state for paid payments from payments table
  const [activeTab, setActiveTab] = useState('outstanding');
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedPr, setSelectedPr] = useState(null);
  const [selectedPayment, setSelectedPayment] = useState(null); // For viewing payment details
  const [markPaidDialogOpen, setMarkPaidDialogOpen] = useState(false);
  const [utrNumber, setUtrNumber] = useState('');
  const [paidDate, setPaidDate] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('NEFT');
  const [paymentAmount, setPaymentAmount] = useState('');  // New state for partial payment amount
  const [loading, setLoading] = useState(false);
  
  // Pagination & Filter states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [costCenterFilter, setCostCenterFilter] = useState('all');
  const [vendorFilter, setVendorFilter] = useState('all');

  // Paid tab specific pagination & filter states
  const [paidCurrentPage, setPaidCurrentPage] = useState(1);
  const [paidItemsPerPage, setPaidItemsPerPage] = useState(10);
  const [paidSearchTerm, setPaidSearchTerm] = useState('');
  const [paidCostCenterFilter, setPaidCostCenterFilter] = useState('all');
  const [paidVendorFilter, setPaidVendorFilter] = useState('all');

  useEffect(() => {
    fetchAllPayments();
  }, []);

  const fetchAllPayments = async () => {
    try {
      const [overdueRes, dueRes, outstandingRes, onHoldRes, partlyPaidRes, paidListRes] = await Promise.all([
        axios.get(`${API}/payments/overdue`, { headers: getAuthHeader() }),
        axios.get(`${API}/payments/due`, { headers: getAuthHeader() }),
        axios.get(`${API}/payments/outstanding`, { headers: getAuthHeader() }),
        axios.get(`${API}/payments/on-hold`, { headers: getAuthHeader() }),
        axios.get(`${API}/payments/partly-paid`, { headers: getAuthHeader() }),
        axios.get(`${API}/payments/paid-list`, { headers: getAuthHeader() })
      ]);

      setOverdue(overdueRes.data);
      setDue(dueRes.data);
      setOutstanding(outstandingRes.data);
      setOnHold(onHoldRes.data);
      setPartlyPaid(partlyPaidRes.data);
      setPaidPayments(paidListRes.data);
    } catch (error) {
      toast.error('Failed to fetch payment data');
    }
  };

  // Helper function to preserve file extension from S3 key
  const getFilenameWithExtension = (s3Key, defaultName) => {
    if (!s3Key) return defaultName;
    
    // Extract original filename from S3 key (e.g., "proforma/uuid_invoice.png" -> "invoice.png")
    const parts = s3Key.split('/');
    const filename = parts[parts.length - 1];
    
    // Extract extension from original filename
    const extensionMatch = filename.match(/\.[^.]+$/);
    const extension = extensionMatch ? extensionMatch[0] : '.pdf';
    
    // Return default name with correct extension
    return defaultName.replace(/\.[^.]*$/, '') + extension;
  };

  const openViewDialog = (pr) => {
    setSelectedPr(pr);
    setSelectedPayment(null);
    setViewDialogOpen(true);
  };

  // Open view dialog for paid payment (fetches PR details from invoice_id)
  const openPaidPaymentViewDialog = async (payment) => {
    try {
      // Fetch PR details using invoice_id
      const response = await axios.get(`${API}/purchase-requests/${payment.invoice_id}`, { headers: getAuthHeader() });
      setSelectedPr(response.data);
      setSelectedPayment(payment);
      setViewDialogOpen(true);
    } catch (error) {
      toast.error('Failed to fetch payment details');
    }
  };

  const openMarkPaidDialog = (pr) => {
    setSelectedPr(pr);
    setUtrNumber('');
    setPaidDate(new Date().toISOString().split('T')[0]); // Set today's date as default
    setPaymentMethod('NEFT'); // Set default payment method
    
    // Check if this is an advance payment pending approval (not yet partially paid)
    const isAdvancePaymentPending = pr.is_advance_payment && 
      (pr.advance_payment_status === 'approved_pp' || pr.advance_payment_status === 'ppa_approved') && 
      !pr.is_partial_paid;
    
    if (isAdvancePaymentPending) {
      // For advance payment, use the advance amount (not total amount)
      setPaymentAmount((pr.advance_amount || 0).toString());
    } else {
      // Calculate the remaining payable amount for partial payments
      let totalPayable = pr.payable_amount || pr.total_amount || pr.base_amount || 0;
      
      // For advance payment final payments, subtract advance amount
      if (pr.is_advance_payment && pr.is_partial_paid) {
        totalPayable = totalPayable - (pr.advance_amount || 0);
      }
      
      // Subtract any partial payments already made
      const totalPaidSoFar = pr.total_paid_amount || 0;
      const remainingBalance = totalPayable - totalPaidSoFar;
      
      setPaymentAmount(remainingBalance.toString()); // Pre-fill with remaining balance
    }
    setMarkPaidDialogOpen(true);
  };

  const handleMarkPaid = async () => {
    if (!utrNumber.trim()) {
      toast.error('UTR number is required');
      return;
    }
    if (!paidDate) {
      toast.error('Paid date is required');
      return;
    }
    if (!paymentAmount || parseFloat(paymentAmount) <= 0) {
      toast.error('Payment amount must be greater than 0');
      return;
    }
    
    // Calculate remaining balance for validation
    let totalPayable = selectedPr.payable_amount || selectedPr.total_amount || selectedPr.base_amount || 0;
    if (selectedPr.is_advance_payment && selectedPr.is_partial_paid) {
      totalPayable = totalPayable - (selectedPr.advance_amount || 0);
    }
    const totalPaidSoFar = selectedPr.total_paid_amount || 0;
    const remainingBalance = totalPayable - totalPaidSoFar;
    
    if (parseFloat(paymentAmount) > remainingBalance) {
      toast.error(`Payment amount cannot exceed remaining balance of ₹${remainingBalance.toLocaleString()}`);
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(
        `${API}/purchase-requests/${selectedPr.id}/mark-paid`,
        { 
          utr_number: utrNumber,
          paid_date: paidDate,
          payment_method: paymentMethod,
          amount: parseFloat(paymentAmount)
        },
        { headers: getAuthHeader() }
      );
      
      if (response.data.is_fully_paid) {
        toast.success('Payment marked as fully paid!');
      } else {
        toast.success(`Partial payment of ₹${parseFloat(paymentAmount).toLocaleString()} recorded. Remaining: ₹${response.data.remaining_balance.toLocaleString()}`);
      }
      
      setMarkPaidDialogOpen(false);
      setUtrNumber('');
      setPaidDate('');
      setPaymentMethod('NEFT');
      setPaymentAmount('');
      fetchAllPayments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to mark payment as paid');
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAdvancePaid = async () => {
    if (!utrNumber.trim()) {
      toast.error('UTR number is required');
      return;
    }
    if (!paidDate) {
      toast.error('Paid date is required');
      return;
    }

    setLoading(true);
    try {
      await axios.post(
        `${API}/purchase-requests/${selectedPr.id}/mark-advance-paid`,
        { 
          utr_number: utrNumber,
          paid_date: paidDate,
          payment_method: paymentMethod
        },
        { headers: getAuthHeader() }
      );
      toast.success('Advance payment marked as paid!');
      setMarkPaidDialogOpen(false);
      setUtrNumber('');
      setPaidDate('');
      setPaymentMethod('NEFT');
      fetchAllPayments();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to mark advance as paid');
    } finally {
      setLoading(false);
    }
  };

  // Get unique cost centers for filter dropdown
  const getUniqueCostCenters = () => {
    const allPayments = [...overdue, ...due, ...outstanding, ...onHold, ...paid];
    const costCenters = new Set();
    allPayments.forEach(pr => {
      if (pr.cost_centre) {
        costCenters.add(pr.cost_centre);
      }
    });
    return Array.from(costCenters).sort();
  };

  const getUniqueVendors = () => {
    const vendors = new Set();
    [overdue, due, outstanding, onHold, paid].forEach(list => {
      list.forEach(pr => {
        if (pr.vendor_name) {
          vendors.add(pr.vendor_name);
        }
      });
    });
    return Array.from(vendors).sort();
  };

  const uniqueCostCenters = getUniqueCostCenters();
  const uniqueVendors = getUniqueVendors();

  // Reset to page 1 when filters change or tab changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, activeTab, costCenterFilter, vendorFilter]);

  const getStatusBadge = (pr, isPaidTab = false) => {
    let statusBadge = null;

    // Determine the main status badge
    if (pr.is_advance_payment && pr.advance_payment_status === 'approved_pp' && !pr.is_partial_paid) {
      // For advance payments with approved_pp status (NOT yet paid), don't show main status badge
      statusBadge = null;
    } else if (pr.payment_status === 'partly_paid') {
      // Partly paid - show "Partly Paid" badge
      statusBadge = <Badge className="bg-amber-100 text-amber-800 inline-flex w-auto">Partly Paid</Badge>;
    } else if (isPaidTab) {
      // In Paid tab, always show "Paid" badge for items that should be there
      if (pr.is_advance_payment && pr.is_partial_paid && pr.advance_payment_status === 'paid' && pr.payment_status !== 'paid') {
        // For partially paid advance requests (advance paid, final not yet paid), don't show main badge
        statusBadge = null;
      } else {
        // For all other items in Paid tab, show "Paid" badge
        statusBadge = <Badge className="bg-green-100 text-green-800 inline-flex w-auto">Paid</Badge>;
      }
    } else if (pr.status === 'paid' || pr.paid_at || pr.payment_status === 'paid') {
      // Check if payment is completed (for other tabs)
      statusBadge = <Badge className="bg-green-100 text-green-800 inline-flex w-auto">Paid</Badge>;
    } else {
      // Check for overdue payments (for Outstanding/Due/Overdue tabs)
      const daysOverdue = pr.due_date ? Math.floor(
        (new Date() - new Date(pr.due_date)) / (1000 * 60 * 60 * 24)
      ) : 0;

      if (daysOverdue > 15) {
        statusBadge = <Badge className="bg-red-100 text-red-800 inline-flex w-auto">{daysOverdue} days overdue</Badge>;
      } else if (daysOverdue > 0) {
        statusBadge = <Badge className="bg-orange-100 text-orange-800 inline-flex w-auto">{daysOverdue} days overdue</Badge>;
      } else {
        statusBadge = <Badge className="bg-blue-100 text-blue-800 inline-flex w-auto">Approved For Payment</Badge>;
      }
    }

    // Return badges with request type indicator
    return (
      <div className="flex flex-col gap-1 items-start">
        {pr?.is_advance_payment && (
          <Badge className="bg-gradient-to-r from-blue-500 to-cyan-500 text-white inline-flex items-center gap-1 px-2 py-0.5 w-auto">
            <CheckCircle className="w-3 h-3" />
            <span className="text-xs font-semibold">AR</span>
          </Badge>
        )}
        {!pr?.is_advance_payment && !pr?.is_upload_invoice && (
          <Badge className="bg-gradient-to-r from-green-500 to-emerald-500 text-white inline-flex items-center gap-1 px-2 py-0.5 w-auto">
            <CheckCircle className="w-3 h-3" />
            <span className="text-xs font-semibold">PR</span>
          </Badge>
        )}
        {pr?.is_upload_invoice && (
          <Badge className="bg-gradient-to-r from-purple-500 to-pink-500 text-white inline-flex items-center gap-1 px-2 py-0.5 w-auto">
            <Upload className="w-3 h-3" />
            <span className="text-xs font-semibold">UI</span>
          </Badge>
        )}
        {statusBadge}
      </div>
    );
  };

  const getAdvanceStatusBadge = (pr) => {
    if (!pr.is_advance_payment) return null;
    
    // Don't show badge if status is none or undefined
    if (!pr.advance_payment_status || pr.advance_payment_status === 'none') return null;
    
    const statusConfig = {
      'pending_ppa': { label: 'Pending PPA', color: 'bg-yellow-100 text-yellow-800' },
      'approved_pp': { label: 'Approved PP', color: 'bg-blue-100 text-blue-800' },
      'ppa_approved': { label: 'PP Approved', color: 'bg-green-100 text-green-800' },
      'ppa_rejected': { label: 'PP Rejected', color: 'bg-red-100 text-red-800' },
      'advance_paid': { label: 'Advance Paid', color: 'bg-emerald-100 text-emerald-800' },
      'paid': { label: 'Advance Paid', color: 'bg-emerald-100 text-emerald-800' }
    };
    
    const config = statusConfig[pr.advance_payment_status];
    if (!config) return null;
    
    return (
      <Badge className={`${config.color} text-xs inline-flex w-auto`}>
        {config.label}
      </Badge>
    );
  };

  const renderTable = (payments, showMarkPaid = false, isPaidTab = false) => {
    // Apply filters
    let filteredPayments = [...payments];

    // Apply search filter
    if (searchTerm) {
      filteredPayments = filteredPayments.filter(pr => 
        pr.vendor_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        pr.entity_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        pr.cost_centre?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        pr.po_number?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply cost center filter
    if (costCenterFilter !== 'all') {
      filteredPayments = filteredPayments.filter(pr => pr.cost_centre === costCenterFilter);
    }

    // Apply vendor filter
    if (vendorFilter !== 'all') {
      filteredPayments = filteredPayments.filter(pr => pr.vendor_name === vendorFilter);
    }

    // Pagination
    const totalPages = Math.ceil(filteredPayments.length / itemsPerPage);
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    const currentPayments = filteredPayments.slice(startIndex, endIndex);

    return (
      <Card data-testid="payment-queue-table">
        <CardHeader>
          <CardTitle className="text-lg font-semibold text-gray-900">
            Payment Queue - {activeTab.charAt(0).toUpperCase() + activeTab.slice(1)}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {/* Filter and Search Controls */}
          <div className="mb-4 flex flex-wrap gap-4 items-center justify-between">
            <div className="flex gap-3 items-center flex-1">
              <input
                type="text"
                placeholder="Search by vendor, entity, cost center, PO number..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 flex-1 max-w-md"
              />
              <select
                value={vendorFilter}
                onChange={(e) => setVendorFilter(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                <option value="all">All Vendors</option>
                {uniqueVendors.map(vendor => (
                  <option key={vendor} value={vendor}>{vendor}</option>
                ))}
              </select>
              <select
                value={costCenterFilter}
                onChange={(e) => setCostCenterFilter(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                <option value="all">All Cost Centers</option>
                {uniqueCostCenters.map(cc => (
                  <option key={cc} value={cc}>{cc}</option>
                ))}
              </select>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">Show:</span>
              <select
                value={itemsPerPage}
                onChange={(e) => {
                  setItemsPerPage(Number(e.target.value));
                  setCurrentPage(1);
                }}
                className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
              >
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
              </select>
              <span className="text-sm text-gray-600">per page</span>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Vendor Name</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Entity</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Cost Center</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Amount</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Payable Amount</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">PO Number</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Due Date</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Finance Approved Date</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Schedule Payment After</th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">Status</th>
                  <th className="px-3 py-3 text-center text-sm font-semibold text-gray-900">View</th>
                  {showMarkPaid && (
                    <th className="px-3 py-3 text-center text-sm font-semibold text-gray-900">Mark as Paid</th>
                  )}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {currentPayments.map((pr) => (
                  <tr key={pr.id}>
                    <td className="px-4 py-3 text-sm text-gray-900">
                      {pr.vendor_name || 'N/A'}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {pr.entity_name || 'N/A'}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {pr.cost_centre}
                    </td>
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">
                      {(() => {
                        // For advance payment requests
                        if (pr.is_advance_payment) {
                          // In Paid tab, show full amount (Advance + Remaining = Total)
                          if (isPaidTab && (pr.status === 'paid' || pr.payment_status === 'paid')) {
                            return `₹${(pr.total_amount || 0).toLocaleString()}`;
                          }
                          // 1st time request (advance not yet paid) - show advance amount
                          if (!pr.is_partial_paid) {
                            return `₹${(pr.advance_amount || 0).toLocaleString()}`;
                          }
                          // 2nd request (after advance paid) - show TOTAL amount (not remaining)
                          else {
                            return `₹${(pr.total_amount || 0).toLocaleString()}`;
                          }
                        }
                        // Regular purchase requests - show total amount
                        return `₹${(pr.total_amount || pr.base_amount || 0).toLocaleString()}`;
                      })()}
                    </td>
                    <td className="px-4 py-3 text-sm font-medium text-blue-700">
                      {(() => {
                        // Calculate total payable amount
                        let totalPayable = 0;
                        
                        if (pr.is_advance_payment) {
                          const isPaidTabCheck = activeTab === 'paid';
                          if (isPaidTabCheck && (pr.status === 'paid' || pr.payment_status === 'paid')) {
                            // In Paid tab - show full payable amount
                            totalPayable = pr.payable_amount || pr.total_amount || 0;
                          } else if (!pr.is_partial_paid) {
                            // 1st time request (advance not yet paid) - show advance amount
                            totalPayable = pr.advance_amount || 0;
                          } else {
                            // 2nd request (after advance paid) - show payable amount MINUS advance
                            // payable_amount includes TDS deduction from total, so we subtract advance from it
                            if (pr.is_tds_applicable === 'Yes' && pr.payable_amount) {
                              totalPayable = (pr.payable_amount || 0) - (pr.advance_amount || 0);
                            } else {
                              totalPayable = (pr.total_amount || 0) - (pr.advance_amount || 0);
                            }
                          }
                        } else if (pr.is_tds_applicable === 'Yes' && pr.payable_amount) {
                          totalPayable = pr.payable_amount || 0;
                        } else {
                          totalPayable = pr.total_amount || pr.base_amount || 0;
                        }
                        
                        // Calculate balance (remaining amount after partial payments)
                        const totalPaidSoFar = pr.total_paid_amount || 0;
                        const balance = totalPayable - totalPaidSoFar;
                        
                        return (
                          <div>
                            <div>₹{totalPayable.toLocaleString()}</div>
                            {totalPaidSoFar > 0 && pr.payment_status !== 'paid' && (
                              <div className="text-xs text-amber-600 font-semibold mt-1">
                                Bal: ₹{balance.toLocaleString()}
                              </div>
                            )}
                          </div>
                        );
                      })()}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {pr.po_number || 'N/A'}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {pr.due_date ? new Date(pr.due_date).toLocaleDateString() : 'N/A'}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {pr.finance_approved_at ? new Date(pr.finance_approved_at).toLocaleDateString() : '-'}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      {pr.scheduled_payment_days && pr.scheduled_payment_date ? (
                        <span className="text-blue-600 font-medium">
                          {pr.scheduled_payment_days} days : {new Date(pr.scheduled_payment_date).toLocaleDateString()}
                        </span>
                      ) : (
                        '-'
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {getStatusBadge(pr, isPaidTab)}
                      {/* 
                        Show advance status badge EXCEPT when:
                        - In Paid tab AND
                        - Main status badge is showing "Paid" AND
                        - This would create duplicate "Paid" + "Advance Paid" display
                        
                        Special case: If main badge is null (partially paid advance), still show advance badge
                      */}
                      {(() => {
                        // Check if main badge would be "Paid" in Paid tab
                        const mainBadgeShowsPaid = isPaidTab && 
                          !(pr.is_advance_payment && pr.is_partial_paid && pr.advance_payment_status === 'paid' && pr.payment_status !== 'paid');
                        
                        // Hide advance badge only when both "Paid" and "Advance Paid" would show
                        const shouldHideAdvanceBadge = mainBadgeShowsPaid && isPaidTab;
                        
                        if (!shouldHideAdvanceBadge && getAdvanceStatusBadge(pr)) {
                          return <div className="mt-1">{getAdvanceStatusBadge(pr)}</div>;
                        }
                        return null;
                      })()}
                      {pr.hold_status && (
                        <div className="mt-1">
                          <Badge className="bg-red-100 text-red-800 w-auto inline-flex">On Hold</Badge>
                        </div>
                      )}
                    </td>
                    <td className="px-3 py-3 text-center">
                      <Button
                        onClick={() => openViewDialog(pr)}
                        variant="ghost"
                        size="sm"
                        className="text-indigo-600 hover:text-indigo-900"
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                    </td>
                    {showMarkPaid && (
                      <td className="px-3 py-3 text-center">
                        {pr.is_advance_payment && (pr.advance_payment_status === 'approved_pp' || pr.advance_payment_status === 'ppa_approved') && !pr.is_partial_paid ? (
                          <Button
                            onClick={() => openMarkPaidDialog(pr)}
                            size="sm"
                            className="bg-amber-600 hover:bg-amber-700 text-white"
                            data-testid={`mark-advance-paid-button-${pr.id}`}
                            title="Mark Advance as Paid"
                          >
                            <DollarSign className="w-4 h-4 mr-1" />
                            Advance
                          </Button>
                        ) : (
                          <Button
                            onClick={() => openMarkPaidDialog(pr)}
                            size="sm"
                            className="bg-green-600 hover:bg-green-700 text-white"
                            data-testid={`mark-paid-button-${pr.id}`}
                          >
                            <CheckCircle className="w-4 h-4" />
                          </Button>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination Controls */}
          {filteredPayments.length > 0 && (
            <div className="mt-4 flex items-center justify-between">
              <p className="text-sm text-gray-600">
                Showing {startIndex + 1} to {Math.min(endIndex, filteredPayments.length)} of {filteredPayments.length} results
              </p>
              <div className="flex gap-2">
                <Button
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                  variant="outline"
                  size="sm"
                >
                  Previous
                </Button>
                <div className="flex gap-1">
                  {Array.from({ length: totalPages }, (_, i) => i + 1)
                    .filter(page => {
                      return page === 1 || 
                             page === totalPages || 
                             (page >= currentPage - 1 && page <= currentPage + 1);
                    })
                    .map((page, index, array) => (
                      <div key={page} className="flex items-center">
                        {index > 0 && array[index - 1] !== page - 1 && (
                          <span className="px-2 text-gray-400">...</span>
                        )}
                        <Button
                          onClick={() => setCurrentPage(page)}
                          variant={currentPage === page ? "default" : "outline"}
                          size="sm"
                          className={currentPage === page ? "bg-indigo-600 hover:bg-indigo-700" : ""}
                        >
                          {page}
                        </Button>
                      </div>
                    ))
                  }
                </div>
                <Button
                  onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages}
                  variant="outline"
                  size="sm"
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <nav className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center h-16">
            <h1 className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Space Grotesk' }}>
              Payment Queue
            </h1>
          </div>
        </div>
      </nav>

      <div className="mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-6 mb-8">
            <TabsTrigger value="overdue" data-testid="tab-overdue">
              Overdue ({overdue.length})
            </TabsTrigger>
            <TabsTrigger value="due" data-testid="tab-due">
              Due ({due.length})
            </TabsTrigger>
            <TabsTrigger value="outstanding" data-testid="tab-outstanding">
              Outstanding ({outstanding.length})
            </TabsTrigger>
            <TabsTrigger value="partly-paid" data-testid="tab-partly-paid">
              Partly Paid ({partlyPaid.length})
            </TabsTrigger>
            <TabsTrigger value="on-hold" data-testid="tab-on-hold">
              On Hold ({onHold.length})
            </TabsTrigger>
            <TabsTrigger value="paid" data-testid="tab-paid">
              Paid ({paidPayments.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="overdue" data-testid="overdue-list">
            {overdue.length === 0 ? (
              <Card className="text-center py-12">
                <CardContent>
                  <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">No overdue payments</h3>
                </CardContent>
              </Card>
            ) : (
              renderTable(overdue, true)
            )}
          </TabsContent>

          <TabsContent value="due" data-testid="due-list">
            {due.length === 0 ? (
              <Card className="text-center py-12">
                <CardContent>
                  <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">No due payments</h3>
                </CardContent>
              </Card>
            ) : (
              renderTable(due, true)
            )}
          </TabsContent>

          <TabsContent value="outstanding" data-testid="outstanding-list">
            {outstanding.length === 0 ? (
              <Card className="text-center py-12">
                <CardContent>
                  <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">No outstanding payments</h3>
                </CardContent>
              </Card>
            ) : (
              renderTable(outstanding, true)
            )}
          </TabsContent>

          <TabsContent value="partly-paid" data-testid="partly-paid-list">
            {partlyPaid.length === 0 ? (
              <Card className="text-center py-12">
                <CardContent>
                  <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">No partly paid requests</h3>
                </CardContent>
              </Card>
            ) : (
              renderTable(partlyPaid, true)
            )}
          </TabsContent>

          <TabsContent value="on-hold" data-testid="on-hold-list">
            {onHold.length === 0 ? (
              <Card className="text-center py-12">
                <CardContent>
                  <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">No payments on hold</h3>
                </CardContent>
              </Card>
            ) : (
              renderTable(onHold, false)
            )}
          </TabsContent>

          <TabsContent value="paid" data-testid="paid-list">
            {paidPayments.length === 0 ? (
              <Card className="text-center py-12">
                <CardContent>
                  <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">No paid payments</h3>
                  <p className="text-gray-600">Completed payments will appear here</p>
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardContent className="p-4">
                  {/* Search and Filters */}
                  <div className="flex flex-wrap gap-4 mb-4">
                    <div className="flex-1 min-w-[200px]">
                      <Input
                        placeholder="Search by PO Number, UTR, Vendor, Entity, Amount, Date, Cost Centre..."
                        value={paidSearchTerm}
                        onChange={(e) => {
                          setPaidSearchTerm(e.target.value);
                          setPaidCurrentPage(1);
                        }}
                        className="w-full"
                      />
                    </div>
                    <div className="w-48">
                      <select
                        value={paidVendorFilter}
                        onChange={(e) => {
                          setPaidVendorFilter(e.target.value);
                          setPaidCurrentPage(1);
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                      >
                        <option value="all">All Vendors</option>
                        {[...new Set(paidPayments.map(p => p.vendor_name).filter(Boolean))].map(vendor => (
                          <option key={vendor} value={vendor}>{vendor}</option>
                        ))}
                      </select>
                    </div>
                    <div className="w-48">
                      <select
                        value={paidCostCenterFilter}
                        onChange={(e) => {
                          setPaidCostCenterFilter(e.target.value);
                          setPaidCurrentPage(1);
                        }}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                      >
                        <option value="all">All Cost Centers</option>
                        {[...new Set(paidPayments.map(p => p.cost_center).filter(Boolean))].map(cc => (
                          <option key={cc} value={cc}>{cc}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Table */}
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className="bg-gray-50 border-b">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">PO Number</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Vendor</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Entity</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Cost Center</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Amount</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Payment Date</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">UTR No.</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Method</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Type</th>
                          <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase">View</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-100">
                        {(() => {
                          // Filter payments
                          let filteredPayments = paidPayments.filter(payment => {
                            const searchLower = paidSearchTerm.toLowerCase();
                            const matchesSearch = paidSearchTerm === '' || 
                              (payment.po_number || '').toLowerCase().includes(searchLower) ||
                              (payment.utr_number || '').toLowerCase().includes(searchLower) ||
                              (payment.vendor_name || '').toLowerCase().includes(searchLower) ||
                              (payment.entity_name || '').toLowerCase().includes(searchLower) ||
                              (payment.cost_center || '').toLowerCase().includes(searchLower) ||
                              (payment.amount?.toString() || '').includes(paidSearchTerm) ||
                              (payment.payment_date ? new Date(payment.payment_date).toLocaleDateString().includes(paidSearchTerm) : false);
                            const matchesVendor = paidVendorFilter === 'all' || payment.vendor_name === paidVendorFilter;
                            const matchesCostCenter = paidCostCenterFilter === 'all' || payment.cost_center === paidCostCenterFilter;
                            return matchesSearch && matchesVendor && matchesCostCenter;
                          });

                          // Paginate
                          const startIndex = (paidCurrentPage - 1) * paidItemsPerPage;
                          const paginatedPayments = filteredPayments.slice(startIndex, startIndex + paidItemsPerPage);

                          if (paginatedPayments.length === 0) {
                            return (
                              <tr>
                                <td colSpan="10" className="px-4 py-8 text-center text-gray-500">
                                  No payments found matching your filters
                                </td>
                              </tr>
                            );
                          }

                          return paginatedPayments.map((payment) => (
                            <tr key={payment.id} className="hover:bg-gray-50 transition-colors">
                              <td className="px-4 py-3 text-sm font-medium text-blue-600">{payment.po_number || 'N/A'}</td>
                              <td className="px-4 py-3 text-sm text-gray-900">{payment.vendor_name || 'N/A'}</td>
                              <td className="px-4 py-3 text-sm text-gray-600">{payment.entity_name || 'N/A'}</td>
                              <td className="px-4 py-3 text-sm text-gray-600">{payment.cost_center || 'N/A'}</td>
                              <td className="px-4 py-3 text-sm font-medium text-green-700">₹{(payment.amount || 0).toLocaleString()}</td>
                              <td className="px-4 py-3 text-sm text-gray-600">
                                {payment.payment_date ? new Date(payment.payment_date).toLocaleDateString() : 'N/A'}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-600">{payment.utr_number || 'N/A'}</td>
                              <td className="px-4 py-3 text-sm text-gray-600">{payment.payment_method || 'N/A'}</td>
                              <td className="px-4 py-3">
                                <Badge className={
                                  payment.payment_type === 'AD' ? 'bg-gradient-to-r from-blue-500 to-cyan-500 text-white inline-flex items-center gap-1 px-2 py-0.5' :
                                  payment.payment_type === 'UI' ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white inline-flex items-center gap-1 px-2 py-0.5' :
                                  'bg-gradient-to-r from-green-500 to-emerald-500 text-white inline-flex items-center gap-1 px-2 py-0.5'
                                }>
                                  {payment.payment_type === 'UI' ? (
                                    <Upload className="w-3 h-3" />
                                  ) : (
                                    <CheckCircle className="w-3 h-3" />
                                  )}
                                  <span className="text-xs font-semibold">
                                    {payment.payment_type === 'AD' ? 'AR' : payment.payment_type}
                                  </span>
                                </Badge>
                              </td>
                              <td className="px-4 py-3 text-center">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => openPaidPaymentViewDialog(payment)}
                                  className="text-indigo-600 hover:text-indigo-800"
                                >
                                  <Eye className="w-4 h-4" />
                                </Button>
                              </td>
                            </tr>
                          ));
                        })()}
                      </tbody>
                    </table>
                  </div>

                  {/* Pagination */}
                  {(() => {
                    const searchLower = paidSearchTerm.toLowerCase();
                    const filteredPayments = paidPayments.filter(payment => {
                      const matchesSearch = paidSearchTerm === '' || 
                        (payment.po_number || '').toLowerCase().includes(searchLower) ||
                        (payment.utr_number || '').toLowerCase().includes(searchLower) ||
                        (payment.vendor_name || '').toLowerCase().includes(searchLower) ||
                        (payment.entity_name || '').toLowerCase().includes(searchLower) ||
                        (payment.cost_center || '').toLowerCase().includes(searchLower) ||
                        (payment.amount?.toString() || '').includes(paidSearchTerm) ||
                        (payment.payment_date ? new Date(payment.payment_date).toLocaleDateString().includes(paidSearchTerm) : false);
                      const matchesVendor = paidVendorFilter === 'all' || payment.vendor_name === paidVendorFilter;
                      const matchesCostCenter = paidCostCenterFilter === 'all' || payment.cost_center === paidCostCenterFilter;
                      return matchesSearch && matchesVendor && matchesCostCenter;
                    });
                    const totalPages = Math.ceil(filteredPayments.length / paidItemsPerPage) || 1;
                    const startIndex = (paidCurrentPage - 1) * paidItemsPerPage;
                    const endIndex = Math.min(startIndex + paidItemsPerPage, filteredPayments.length);

                    return (
                      <div className="flex items-center justify-between mt-4 pt-4 border-t" data-testid="paid-tab-pagination">
                        <div className="flex items-center gap-4">
                          <div className="text-sm text-gray-600">
                            Showing {filteredPayments.length > 0 ? startIndex + 1 : 0} to {endIndex} of {filteredPayments.length} payments
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-sm text-gray-600">Show:</span>
                            <select
                              value={paidItemsPerPage}
                              onChange={(e) => {
                                setPaidItemsPerPage(Number(e.target.value));
                                setPaidCurrentPage(1);
                              }}
                              className="px-2 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                              data-testid="paid-items-per-page"
                            >
                              <option value={5}>5</option>
                              <option value={10}>10</option>
                              <option value={25}>25</option>
                              <option value={50}>50</option>
                            </select>
                            <span className="text-sm text-gray-600">per page</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setPaidCurrentPage(p => Math.max(1, p - 1))}
                            disabled={paidCurrentPage === 1}
                            data-testid="paid-prev-page"
                          >
                            Previous
                          </Button>
                          <span className="text-sm text-gray-600">
                            Page {paidCurrentPage} of {totalPages}
                          </span>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setPaidCurrentPage(p => Math.min(totalPages, p + 1))}
                            disabled={paidCurrentPage === totalPages}
                            data-testid="paid-next-page"
                          >
                            Next
                          </Button>
                        </div>
                      </div>
                    );
                  })()}
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* View Details Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Payment Details</DialogTitle>
            <DialogDescription>
              Complete payment information and document trail
            </DialogDescription>
          </DialogHeader>
          {selectedPr && (
            <div className="space-y-6">
              {/* Status Badges */}
              <div className="flex items-center gap-2">
                {getStatusBadge(selectedPr)}
                {selectedPr.hold_status && (
                  <Badge className="bg-red-100 text-red-800">On Hold</Badge>
                )}
              </div>

              {/* Hold Reason if present */}
              {selectedPr.hold_status && selectedPr.hold_reason && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <p className="text-sm font-medium text-red-800 mb-2">Hold Reason:</p>
                  <p className="text-sm text-red-700">{selectedPr.hold_reason}</p>
                  {selectedPr.held_by && selectedPr.held_at && (
                    <p className="text-xs text-red-600 mt-2">
                      Held by: {selectedPr.held_by} on {new Date(selectedPr.held_at).toLocaleDateString()}
                    </p>
                  )}
                </div>
              )}

              {/* Basic Information */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-600">Requested By</p>
                  <p className="text-sm font-medium mt-1">{selectedPr.requested_by}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Cost Centre</p>
                  <p className="text-sm font-medium mt-1">{selectedPr.cost_centre}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Vendor Name</p>
                  <p className="text-sm font-medium mt-1">{selectedPr.vendor_name || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Entity</p>
                  <p className="text-sm font-medium mt-1">{selectedPr.entity_name || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Tax Inclusive</p>
                  <p className="text-sm font-medium mt-1">{selectedPr.inclusive_tax || 'No'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">PO Number</p>
                  <p className="text-sm font-medium mt-1">{selectedPr.po_number || 'Not Generated'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Base Amount</p>
                  <p className="text-sm font-medium mt-1">₹{(selectedPr.base_amount || 0).toLocaleString()}</p>
                </div>
                {selectedPr.inclusive_tax === 'Yes' && (
                  <>
                    <div>
                      <p className="text-sm text-gray-600">Tax Amount</p>
                      <p className="text-sm font-medium mt-1">₹{(selectedPr.tax_amount || 0).toLocaleString()}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Total Amount</p>
                      <p className="text-sm font-medium mt-1 text-green-700">₹{(selectedPr.total_amount || selectedPr.base_amount || 0).toLocaleString()}</p>
                    </div>
                  </>
                )}
                {selectedPr.is_tds_applicable === 'Yes' && (
                  <>
                    <div>
                      <p className="text-sm text-gray-600">TDS Percentage</p>
                      <p className="text-sm font-medium mt-1">{selectedPr.tds_percentage || 0}%</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Payable Amount</p>
                      <p className="text-sm font-medium mt-1 text-blue-700 font-semibold">
                        ₹{(selectedPr.payable_amount || 0).toLocaleString()}
                      </p>
                    </div>
                  </>
                )}
                <div>
                  <p className="text-sm text-gray-600">Reason</p>
                  <p className="text-sm font-medium mt-1">{selectedPr.reason || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-600">Due Date</p>
                  <p className="text-sm font-medium mt-1">
                    {selectedPr.due_date ? new Date(selectedPr.due_date).toLocaleDateString() : 'N/A'}
                  </p>
                </div>
                {selectedPr.finance_approved_at && (
                  <div>
                    <p className="text-sm text-gray-600">Finance Approved Date</p>
                    <p className="text-sm font-medium mt-1">
                      {new Date(selectedPr.finance_approved_at).toLocaleDateString()}
                    </p>
                  </div>
                )}
                {selectedPr.scheduled_payment_days && selectedPr.scheduled_payment_date && (
                  <div>
                    <p className="text-sm text-gray-600">Schedule Payment After</p>
                    <p className="text-sm font-medium mt-1">
                      {selectedPr.scheduled_payment_days} days : {new Date(selectedPr.scheduled_payment_date).toLocaleDateString()}
                    </p>
                  </div>
                )}
                {selectedPr.paid_at && (
                  <div>
                    <p className="text-sm text-gray-600">Paid On</p>
                    <p className="text-sm font-medium mt-1">
                      {new Date(selectedPr.paid_at).toLocaleDateString()}
                    </p>
                  </div>
                )}
              </div>

              {/* Partial Payment Info Section - Show if any partial payments made */}
              {((selectedPr.partial_payments && selectedPr.partial_payments.length > 0) || (selectedPr.total_paid_amount && selectedPr.total_paid_amount > 0)) && (
                <div className="bg-amber-50 rounded-lg p-4 space-y-3 border-2 border-amber-200">
                  <div className="flex items-center justify-between">
                    <h3 className="font-semibold text-amber-900">Partial Payment Info</h3>
                    <Badge className="bg-amber-100 text-amber-800">
                      {selectedPr.payment_status === 'paid' ? 'Fully Paid' : 'Partly Paid'}
                    </Badge>
                  </div>
                  
                  {/* Balance Amount Summary */}
                  <div className="grid grid-cols-3 gap-4 bg-white p-3 rounded-lg">
                    <div>
                      <p className="text-xs text-gray-600">Payable Amount</p>
                      <p className="text-sm font-bold text-gray-900">
                        ₹{(() => {
                          let totalPayable = selectedPr.payable_amount || selectedPr.total_amount || selectedPr.base_amount || 0;
                          if (selectedPr.is_advance_payment && selectedPr.is_partial_paid) {
                            totalPayable = totalPayable - (selectedPr.advance_amount || 0);
                          }
                          return totalPayable.toLocaleString();
                        })()}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-600">Total Paid</p>
                      <p className="text-sm font-bold text-green-700">
                        ₹{(selectedPr.total_paid_amount || 0).toLocaleString()}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-600">Balance Amount</p>
                      <p className="text-sm font-bold text-amber-700">
                        ₹{(() => {
                          let totalPayable = selectedPr.payable_amount || selectedPr.total_amount || selectedPr.base_amount || 0;
                          if (selectedPr.is_advance_payment && selectedPr.is_partial_paid) {
                            totalPayable = totalPayable - (selectedPr.advance_amount || 0);
                          }
                          return (totalPayable - (selectedPr.total_paid_amount || 0)).toLocaleString();
                        })()}
                      </p>
                    </div>
                  </div>

                  {/* Payment History */}
                  {selectedPr.partial_payments && selectedPr.partial_payments.length > 0 && (
                    <div className="space-y-2">
                      <p className="text-sm font-medium text-amber-900">Payment History:</p>
                      <div className="space-y-2 max-h-40 overflow-y-auto">
                        {selectedPr.partial_payments.map((payment, index) => (
                          <div key={payment.id || index} className="bg-white p-3 rounded-lg border border-amber-200">
                            <div className="flex justify-between items-start">
                              <div>
                                <p className="text-sm font-semibold text-gray-900">
                                  Payment #{index + 1}: ₹{(payment.amount || 0).toLocaleString()}
                                </p>
                                <p className="text-xs text-gray-600">
                                  UTR: {payment.utr_number} | {payment.payment_method}
                                </p>
                              </div>
                              <div className="text-right">
                                <p className="text-xs text-gray-600">
                                  {payment.paid_at ? new Date(payment.paid_at).toLocaleDateString() : 'N/A'}
                                </p>
                                <p className="text-xs text-gray-500">
                                  by {payment.paid_by}
                                </p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Advance Payment Details Section */}
              {selectedPr.is_advance_payment && (
                <div className="bg-amber-50 rounded-lg p-4 space-y-3 border-2 border-amber-200">
                  <div className="flex items-center justify-between">
                    <h3 className="font-semibold text-amber-900">Advance Payment Details</h3>
                    {getAdvanceStatusBadge(selectedPr)}
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-amber-700">Total Amount</Label>
                      <p className="text-sm font-bold text-amber-900">₹{(selectedPr.total_amount || selectedPr.base_amount || 0).toLocaleString()}</p>
                    </div>
                    <div>
                      <Label className="text-amber-700">Advance Payment</Label>
                      <p className="text-sm font-bold text-green-900 bg-green-50 p-2 rounded">₹{(selectedPr.advance_amount || 0).toLocaleString()}</p>
                    </div>
                    <div>
                      <Label className="text-amber-700">Payment Status</Label>
                      <p className="text-sm font-medium text-amber-900">
                        {selectedPr.is_partial_paid ? 'Advance Paid' : 'Advance Pending'}
                      </p>
                    </div>
                    {selectedPr.advance_approved_by && (
                      <div>
                        <Label className="text-amber-700">PPA Approved By</Label>
                        <p className="text-sm font-medium text-amber-900">{selectedPr.advance_approved_by}</p>
                      </div>
                    )}
                    {selectedPr.advance_approved_at && (
                      <div>
                        <Label className="text-amber-700">PPA Approved At</Label>
                        <p className="text-sm font-medium text-amber-900">
                          {new Date(selectedPr.advance_approved_at).toLocaleString()}
                        </p>
                      </div>
                    )}
                    {selectedPr.advance_rejected_by && (
                      <div>
                        <Label className="text-red-700">PPA Rejected By</Label>
                        <p className="text-sm font-medium text-red-900">{selectedPr.advance_rejected_by}</p>
                      </div>
                    )}
                    {selectedPr.advance_rejection_reason && (
                      <div className="col-span-2">
                        <Label className="text-red-700">Rejection Reason</Label>
                        <p className="text-sm text-red-900 p-2 bg-red-50 rounded">{selectedPr.advance_rejection_reason}</p>
                      </div>
                    )}
                    {selectedPr.advance_paid_at && (
                      <div>
                        <Label className="text-green-700">Advance Paid On</Label>
                        <p className="text-sm font-medium text-green-900">
                          {new Date(selectedPr.advance_paid_at).toLocaleDateString()}
                        </p>
                      </div>
                    )}
                    {selectedPr.advance_utr_number && (
                      <div>
                        <Label className="text-green-700">Advance UTR Number</Label>
                        <p className="text-sm font-medium text-green-900">{selectedPr.advance_utr_number}</p>
                      </div>
                    )}
                  </div>
                  <div className="bg-amber-100 p-3 rounded mt-2">
                    <p className="text-xs text-amber-800">
                      <strong>Note:</strong> This is an advance payment request. After advance is paid and delivery is confirmed, 
                      the pending amount (₹{((selectedPr.total_amount || selectedPr.base_amount || 0) - (selectedPr.advance_amount || 0)).toLocaleString()}) will require separate approval.
                    </p>
                  </div>
                </div>
              )}

              {/* Tax Invoice Details Section - Only show if there's invoice data */}
              {(selectedPr.tax_invoice_date || selectedPr.credit_period || selectedPr.due_date || selectedPr.utr_number) && (
                <div className="border-t pt-4">
                  <h3 className="text-base font-semibold text-gray-900 mb-3">Tax Invoice Details</h3>
                  <div className="bg-green-50 rounded-lg p-4 space-y-3">
                    <div className="grid grid-cols-3 gap-4 text-sm">
                    {selectedPr.tax_invoice_date && (
                      <div>
                        <span className="font-medium text-gray-700">Invoice Date:</span>
                        <p className="text-gray-900 mt-1">
                          {new Date(selectedPr.tax_invoice_date).toLocaleDateString()}
                        </p>
                      </div>
                    )}
                    {selectedPr.credit_period && (
                      <div>
                        <span className="font-medium text-gray-700">Credit Period:</span>
                        <p className="text-gray-900 mt-1">{selectedPr.credit_period} days</p>
                      </div>
                    )}
                    {selectedPr.due_date && (
                      <div>
                        <span className="font-medium text-gray-700">Due Date:</span>
                        <p className="text-gray-900 mt-1">
                          {new Date(selectedPr.due_date).toLocaleDateString()}
                        </p>
                      </div>
                    )}
                    {selectedPr.utr_number && (
                      <div>
                        <span className="font-medium text-gray-700">UTR Number:</span>
                        <p className="text-gray-900 mt-1">{selectedPr.utr_number}</p>
                      </div>
                    )}
                    </div>
                  </div>
                </div>
              )}

              {/* Documents Section */}
              <div className="border-t pt-4">
                <h3 className="text-base font-semibold text-gray-900 mb-3">Documents</h3>
                {(selectedPr.proforma_invoice_url || selectedPr.tax_invoice_url || selectedPr.po_url) ? (
                  <div className="space-y-3">
                    {selectedPr.proforma_invoice_url && (
                      <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div>
                          <p className="text-sm font-medium text-gray-900">Proforma Invoice</p>
                          <p className="text-xs text-gray-600">Purchase order document</p>
                        </div>
                        <Button
                          onClick={() => downloadFile(
                            selectedPr.proforma_invoice_url, 
                            getFilenameWithExtension(selectedPr.proforma_invoice_url, `Proforma_Invoice_${selectedPr.id}.pdf`)
                          )}
                          variant="outline"
                          size="sm"
                          className="text-indigo-600 hover:text-indigo-700"
                        >
                          <Download className="w-4 h-4 mr-2" />
                          Download
                        </Button>
                      </div>
                    )}
                    {selectedPr.tax_invoice_url && (
                      <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div>
                          <p className="text-sm font-medium text-gray-900">Tax Invoice</p>
                          <p className="text-xs text-gray-600">Uploaded tax invoice</p>
                        </div>
                        <Button
                          onClick={() => downloadFile(
                            selectedPr.tax_invoice_url, 
                            getFilenameWithExtension(selectedPr.tax_invoice_url, `Tax_Invoice_${selectedPr.id}.pdf`)
                          )}
                          variant="outline"
                          size="sm"
                          className="text-indigo-600 hover:text-indigo-700"
                        >
                          <Download className="w-4 h-4 mr-2" />
                          Download
                        </Button>
                      </div>
                    )}
                    {selectedPr.po_url && (
                      <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div>
                          <p className="text-sm font-medium text-gray-900">Purchase Order</p>
                          <p className="text-xs text-gray-600">Generated PO: {selectedPr.po_number || 'N/A'}</p>
                        </div>
                        <Button
                          onClick={() => downloadFile(selectedPr.po_url, `PO_${selectedPr.po_number || selectedPr.id}.pdf`)}
                          variant="outline"
                          size="sm"
                          className="text-indigo-600 hover:text-indigo-700"
                        >
                          <Download className="w-4 h-4 mr-2" />
                          Download
                        </Button>
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500">No documents uploaded</p>
                )}
              </div>

              {/* Document Trail */}
              <div className="border-t pt-4">
                <h3 className="text-base font-semibold text-gray-900 mb-3">Document Trail</h3>
                <div className="space-y-2">
                  <div className="flex items-start">
                    <CheckCircle className="w-4 h-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                    <div>
                      <div className="font-medium text-gray-900 text-sm">Purchase Request Created</div>
                      <div className="text-xs text-gray-500">
                        by {selectedPr.requested_by} on {new Date(selectedPr.created_at).toLocaleDateString('en-US', { 
                          year: 'numeric', month: 'short', day: 'numeric', 
                          hour: '2-digit', minute: '2-digit' 
                        })}
                      </div>
                    </div>
                  </div>
                  {selectedPr.approved_by && selectedPr.approved_at && (
                    <div className="flex items-start">
                      <CheckCircle className="w-4 h-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                      <div>
                        <div className="font-medium text-gray-900 text-sm">Function Approved</div>
                        <div className="text-xs text-gray-500">
                          by {selectedPr.approved_by} on {new Date(selectedPr.approved_at).toLocaleDateString('en-US', { 
                            year: 'numeric', month: 'short', day: 'numeric', 
                            hour: '2-digit', minute: '2-digit' 
                          })}
                        </div>
                      </div>
                    </div>
                  )}
                  {selectedPr.po_number && (
                    <div className="flex items-start">
                      <CheckCircle className="w-4 h-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                      <div>
                        <div className="font-medium text-gray-900 text-sm">PO Generated</div>
                        <div className="text-xs text-gray-500">
                          PO #{selectedPr.po_number}
                        </div>
                      </div>
                    </div>
                  )}
                  {selectedPr.tax_invoice_url && selectedPr.tax_invoice_date && (
                    <div className="flex items-start">
                      <CheckCircle className="w-4 h-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                      <div>
                        <div className="font-medium text-gray-900 text-sm">Tax Invoice Uploaded</div>
                        <div className="text-xs text-gray-500">
                          by {selectedPr.requested_by} on {new Date(selectedPr.tax_invoice_date).toLocaleDateString('en-US', { 
                            year: 'numeric', month: 'short', day: 'numeric', 
                            hour: '2-digit', minute: '2-digit' 
                          })}
                        </div>
                      </div>
                    </div>
                  )}
                  {selectedPr.accounts_approved_by && selectedPr.accounts_approved_at && (
                    <div className="flex items-start">
                      <CheckCircle className="w-4 h-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                      <div>
                        <div className="font-medium text-gray-900 text-sm">Accounts Approved</div>
                        <div className="text-xs text-gray-500">
                          by {selectedPr.accounts_approved_by} on {new Date(selectedPr.accounts_approved_at).toLocaleDateString('en-US', { 
                            year: 'numeric', month: 'short', day: 'numeric', 
                            hour: '2-digit', minute: '2-digit' 
                          })}
                        </div>
                      </div>
                    </div>
                  )}
                  {selectedPr.finance_approved_by && selectedPr.finance_approved_at && (
                    <div className="flex items-start">
                      <CheckCircle className="w-4 h-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                      <div>
                        <div className="font-medium text-gray-900 text-sm">Finance Approved</div>
                        <div className="text-xs text-gray-500">
                          by {selectedPr.finance_approved_by} on {new Date(selectedPr.finance_approved_at).toLocaleDateString('en-US', { 
                            year: 'numeric', month: 'short', day: 'numeric', 
                            hour: '2-digit', minute: '2-digit' 
                          })}
                        </div>
                      </div>
                    </div>
                  )}
                  {selectedPr.paid_at && (
                    <div className="flex items-start">
                      <CheckCircle className="w-4 h-4 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                      <div>
                        <div className="font-medium text-gray-900 text-sm">Payment Completed</div>
                        <div className="text-xs text-gray-500">
                          by {selectedPr.paid_by} on {new Date(selectedPr.paid_at).toLocaleDateString('en-US', { 
                            year: 'numeric', month: 'short', day: 'numeric', 
                            hour: '2-digit', minute: '2-digit' 
                          })}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Mark as Paid Dialog */}
      <Dialog open={markPaidDialogOpen} onOpenChange={setMarkPaidDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {selectedPr?.is_advance_payment && (selectedPr?.advance_payment_status === 'approved_pp' || selectedPr?.advance_payment_status === 'ppa_approved') && !selectedPr?.is_partial_paid 
                ? 'Mark Advance Payment as Paid' 
                : 'Mark Payment as Paid'}
            </DialogTitle>
            <DialogDescription>
              {selectedPr?.is_advance_payment && (selectedPr?.advance_payment_status === 'approved_pp' || selectedPr?.advance_payment_status === 'ppa_approved') && !selectedPr?.is_partial_paid
                ? 'Enter the UTR number to mark the advance payment as completed'
                : 'Enter the UTR number to mark this payment as completed'}
            </DialogDescription>
          </DialogHeader>
          {selectedPr && (
            <div className="space-y-4">
              {selectedPr.is_advance_payment && (selectedPr.advance_payment_status === 'approved_pp' || selectedPr.advance_payment_status === 'ppa_approved') && !selectedPr.is_partial_paid && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <p className="text-sm font-medium text-amber-900">Advance Payment</p>
                  <p className="text-xs text-amber-800 mt-1">
                    This is an advance payment. After marking as paid, the requester can confirm delivery and remaining amount will be processed separately.
                  </p>
                </div>
              )}
              
              {selectedPr.is_advance_payment && selectedPr.is_partial_paid && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <p className="text-sm font-medium text-blue-900">Final Payment (Advance Already Paid)</p>
                  <p className="text-xs text-blue-800 mt-1">
                    This is the final payment. The advance payment of ₹{(selectedPr.advance_amount || 0).toLocaleString()} has already been processed.
                  </p>
                </div>
              )}
              
              <div className={`rounded-lg p-4 ${selectedPr.is_advance_payment && (selectedPr.advance_payment_status === 'approved_pp' || selectedPr.advance_payment_status === 'ppa_approved') && !selectedPr.is_partial_paid ? 'bg-amber-50' : selectedPr.is_partial_paid ? 'bg-blue-50' : 'bg-gray-50'}`}>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-gray-600">PO Number</p>
                    <p className="font-medium text-gray-900">{selectedPr.po_number}</p>
                  </div>
                  <div>
                    <p className="text-gray-600">
                      {selectedPr.is_advance_payment && (selectedPr.advance_payment_status === 'approved_pp' || selectedPr.advance_payment_status === 'ppa_approved') && !selectedPr.is_partial_paid
                        ? 'Advance Amount' 
                        : selectedPr.is_partial_paid && selectedPr.is_advance_payment
                        ? 'Payable Amount (Excl. Advance)'
                        : 'Payable Amount'}
                    </p>
                    <p className="font-bold text-gray-900">
                      ₹{(() => {
                        let totalPayable = selectedPr.payable_amount || selectedPr.total_amount || selectedPr.base_amount || 0;
                        if (selectedPr.is_advance_payment && (selectedPr.advance_payment_status === 'approved_pp' || selectedPr.advance_payment_status === 'ppa_approved') && !selectedPr.is_partial_paid) {
                          return (selectedPr.advance_amount || 0).toLocaleString();
                        }
                        if (selectedPr.is_advance_payment && selectedPr.is_partial_paid) {
                          totalPayable = totalPayable - (selectedPr.advance_amount || 0);
                        }
                        return totalPayable.toLocaleString();
                      })()}
                    </p>
                  </div>
                  {/* Show partial payment info if any payments have been made */}
                  {(selectedPr.total_paid_amount || 0) > 0 && (
                    <>
                      <div>
                        <p className="text-gray-600">Already Paid</p>
                        <p className="font-medium text-green-700">₹{(selectedPr.total_paid_amount || 0).toLocaleString()}</p>
                      </div>
                      <div>
                        <p className="text-gray-600">Remaining Balance</p>
                        <p className="font-bold text-amber-700">
                          ₹{(() => {
                            let totalPayable = selectedPr.payable_amount || selectedPr.total_amount || selectedPr.base_amount || 0;
                            if (selectedPr.is_advance_payment && selectedPr.is_partial_paid) {
                              totalPayable = totalPayable - (selectedPr.advance_amount || 0);
                            }
                            return (totalPayable - (selectedPr.total_paid_amount || 0)).toLocaleString();
                          })()}
                        </p>
                      </div>
                    </>
                  )}
                  {selectedPr.is_advance_payment && (selectedPr.advance_payment_status === 'approved_pp' || selectedPr.advance_payment_status === 'ppa_approved') && !selectedPr.is_partial_paid && (
                    <>
                      <div>
                        <p className="text-gray-600">Total Amount</p>
                        <p className="font-medium text-gray-900">₹{(selectedPr.total_amount || selectedPr.base_amount || 0).toLocaleString()}</p>
                      </div>
                      <div>
                        <p className="text-gray-600">Pending After Advance</p>
                        <p className="font-medium text-gray-900">
                          ₹{((selectedPr.total_amount || selectedPr.base_amount || 0) - (selectedPr.advance_amount || 0)).toLocaleString()}
                        </p>
                      </div>
                    </>
                  )}
                  {selectedPr.is_advance_payment && selectedPr.is_partial_paid && !(selectedPr.total_paid_amount > 0) && (
                    <>
                      <div>
                        <p className="text-gray-600">Advance Already Paid</p>
                        <p className="font-medium text-green-700">₹{(selectedPr.advance_amount || 0).toLocaleString()}</p>
                      </div>
                      <div>
                        <p className="text-gray-600">Remaining Balance</p>
                        <p className="font-bold text-blue-900">
                          ₹{((selectedPr.total_amount || selectedPr.base_amount || 0) - (selectedPr.advance_amount || 0)).toLocaleString()}
                        </p>
                      </div>
                    </>
                  )}
                  <div>
                    <p className="text-gray-600">Requested By</p>
                    <p className="font-medium text-gray-900">{selectedPr.requested_by}</p>
                  </div>
                  <div>
                    <p className="text-gray-600">Cost Centre</p>
                    <p className="font-medium text-gray-900">{selectedPr.cost_centre}</p>
                  </div>
                </div>
              </div>

              {/* Payment Amount Field - Editable for partial payments, Read-only for advance payments */}
              <div>
                <Label htmlFor="paymentAmount" className="text-sm font-medium">
                  Amount <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="paymentAmount"
                  type="number"
                  value={paymentAmount}
                  onChange={(e) => setPaymentAmount(e.target.value)}
                  placeholder="Enter payment amount"
                  className={`mt-1 ${selectedPr?.is_advance_payment && (selectedPr?.advance_payment_status === 'approved_pp' || selectedPr?.advance_payment_status === 'ppa_approved') && !selectedPr?.is_partial_paid ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                  min="0"
                  step="0.01"
                  readOnly={selectedPr?.is_advance_payment && (selectedPr?.advance_payment_status === 'approved_pp' || selectedPr?.advance_payment_status === 'ppa_approved') && !selectedPr?.is_partial_paid}
                />
                {selectedPr?.is_advance_payment && (selectedPr?.advance_payment_status === 'approved_pp' || selectedPr?.advance_payment_status === 'ppa_approved') && !selectedPr?.is_partial_paid ? (
                  <p className="text-xs text-amber-600 mt-1">
                    Advance payment amount is fixed and cannot be modified.
                  </p>
                ) : (
                  <p className="text-xs text-gray-500 mt-1">
                    You can enter a partial amount. Max: ₹{(() => {
                      let totalPayable = selectedPr.payable_amount || selectedPr.total_amount || selectedPr.base_amount || 0;
                      if (selectedPr.is_advance_payment && selectedPr.is_partial_paid) {
                        totalPayable = totalPayable - (selectedPr.advance_amount || 0);
                      }
                      return (totalPayable - (selectedPr.total_paid_amount || 0)).toLocaleString();
                    })()}
                  </p>
                )}
              </div>

              <div>
                <Label htmlFor="utr" className="text-sm font-medium">
                  UTR Number <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="utr"
                  value={utrNumber}
                  onChange={(e) => setUtrNumber(e.target.value)}
                  placeholder="Enter UTR number"
                  className="mt-1"
                />
              </div>

              <div>
                <Label htmlFor="paidDate" className="text-sm font-medium">
                  Paid On Date <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="paidDate"
                  type="date"
                  value={paidDate}
                  onChange={(e) => setPaidDate(e.target.value)}
                  max={new Date().toISOString().split('T')[0]}
                  className="mt-1"
                />
              </div>

              <div>
                <Label htmlFor="paymentMethod" className="text-sm font-medium">
                  Payment Method <span className="text-red-500">*</span>
                </Label>
                <select
                  id="paymentMethod"
                  value={paymentMethod}
                  onChange={(e) => setPaymentMethod(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 mt-1"
                >
                  <option value="NEFT">NEFT</option>
                  <option value="RTGS">RTGS</option>
                  <option value="IMPS">IMPS</option>
                  <option value="UPI">UPI</option>
                  <option value="CHECK">CHECK</option>
                  <option value="OTHER">OTHER</option>
                </select>
              </div>

              <div className="flex justify-end gap-2">
                <Button
                  onClick={() => setMarkPaidDialogOpen(false)}
                  variant="outline"
                  disabled={loading}
                >
                  Cancel
                </Button>
                <Button
                  onClick={selectedPr?.is_advance_payment && (selectedPr?.advance_payment_status === 'approved_pp' || selectedPr?.advance_payment_status === 'ppa_approved') && !selectedPr?.is_partial_paid
                    ? handleMarkAdvancePaid
                    : handleMarkPaid}
                  className={selectedPr?.is_advance_payment && (selectedPr?.advance_payment_status === 'approved_pp' || selectedPr?.advance_payment_status === 'ppa_approved') && !selectedPr?.is_partial_paid
                    ? 'bg-amber-600 hover:bg-amber-700'
                    : 'bg-green-600 hover:bg-green-700'}
                  disabled={loading || !utrNumber.trim()}
                >
                  {loading ? 'Processing...' : 
                   selectedPr?.is_advance_payment && (selectedPr?.advance_payment_status === 'approved_pp' || selectedPr?.advance_payment_status === 'ppa_approved') && !selectedPr?.is_partial_paid
                     ? 'Mark Advance as Paid'
                     : 'Mark as Paid'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PaymentQueue;
